import { UseMutationOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useUpdateOrganizationSettings } from '../services/react-query/organization';
import { Organization, OrganizationSettings } from '../types/organization';
import { useDebounce } from './useDebounce';

interface Options<TSettings, TData, TError> extends UseMutationOptions<TData, TError, Partial<TSettings>> {
  setSettings: (updates: Partial<TSettings>, options?: UseMutationOptions<TData, TError, Partial<TSettings>>) => void;
  delay?: number;
}

/**
 * Generic hook to debounce settings updates.
 * Supports full React Query mutation lifecycle callbacks.
 */
export function useDebouncedSettings<TSettings, TData = unknown, TError = unknown>({
  setSettings,
  delay = 500,
  ...mutationOptions
}: Options<TSettings, TData, TError>) {
  const { debounce } = useDebounce(delay);

  const debouncedSet = (updates: Partial<TSettings>) => {
    Object.entries(updates).forEach(([key, value]) => {
      debounce(value as string | number | null).then((debouncedVal) => {
        const next = { ...updates, [key]: debouncedVal } as Partial<TSettings>;
        setSettings(next, mutationOptions);
      });
    });
  };

  return { debouncedSet };
}

/**
 * Organization-specific wrapper for debounced settings updates.
 */
export const useDebouncedOrganizationSettings = (delay = 500, options?: UseMutationOptions<Organization, AxiosError, Partial<OrganizationSettings>>) => {
  const { mutate: updateSettings } = useUpdateOrganizationSettings();

  return useDebouncedSettings<OrganizationSettings, Organization, AxiosError>({
    setSettings: updateSettings,
    delay,
    ...options,
  });
};
